import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, fmtDate, daysUntil, statusLabel } from '../utils/api';

export default function AdminDashboard() {
  const [stats, setStats]     = useState(null);
  const [recent, setRecent]   = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      apiGet('/profiles/stats'),
      apiGet('/profiles?status=submitted'),
      apiGet('/profiles?expiring=true'),
    ]).then(([s, r, e]) => {
      setStats(s);
      setRecent(r.slice(0, 5));
      setExpiring(e.slice(0, 5));
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p>Waste profile overview and compliance tracking</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/admin/profiles/new')}>
          + New Profile
        </button>
      </div>
      <div className="stats-grid">
        <StatCard number={stats?.active || 0}        label="Active Profiles"     onClick={() => navigate('/admin/profiles?status=approved')} />
        <StatCard number={stats?.pending || 0}       label="Pending Review"      onClick={() => navigate('/admin/profiles?status=submitted')} cls="warn" />
        <StatCard number={stats?.expiring_soon || 0} label="Expiring in 90 Days" onClick={() => navigate('/admin/expiring')} cls="warn" />
        <StatCard number={stats?.expired || 0}       label="Expired"             onClick={() => navigate('/admin/profiles?status=expired')} cls="danger" />
        <StatCard number={stats?.rejected || 0}      label="Rejected"            onClick={() => navigate('/admin/profiles?status=rejected')} cls="neutral" />
        <StatCard number={stats?.drafts || 0}        label="Drafts"              onClick={() => navigate('/admin/profiles?status=draft')} cls="neutral" />
      </div>
      <div className="dashboard-grid">
        {/* Pending submissions */}
        <div className="card">
          <div className="card-header">
            <h2>Pending Review</h2>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/profiles?status=submitted')}>
              View all
            </button>
          </div>
          {recent.length === 0 ? (
            <div className="empty-state">
              <div className="icon">✓</div>
              <p>No profiles awaiting review</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Waste Name</th><th>Generator Name</th><th>Submitted</th><th></th>
                </tr></thead>
                <tbody>
                  {recent.map(p => (
                    <tr key={p.id}>
                      <td><strong>{p.waste_name}</strong></td>
                      <td>{p.company_name}</td>
                      <td className="text-muted">{fmtDate(p.submitted_at)}</td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/admin/profiles/${p.id}`)}>
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Expiring profiles */}
        <div className="card">
          <div className="card-header">
            <h2>Expiring Soon</h2>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/expiring')}>
              View all
            </button>
          </div>
          {expiring.length === 0 ? (
            <div className="empty-state">
              <div className="icon">✓</div>
              <p>No profiles expiring in the next 90 days</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr>
                  <th>Profile #</th><th>Waste Name</th><th>Expires</th>
                </tr></thead>
                <tbody>
                  {expiring.map(p => {
                    const days = daysUntil(p.expires_at);
                    return (
                      <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/profiles/${p.id}`)}>
                        <td><span className="mono">{p.profile_number}</span></td>
                        <td>{p.waste_name}</td>
                        <td className={days <= 30 ? 'expiring-soon' : 'text-muted'}>
                          {fmtDate(p.expires_at)}<br/>
                          <span style={{ fontSize: 11 }}>{days} days</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ number, label, cls = '', onClick }) {
  return (
    <div
      className={`stat-card ${cls}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="stat-number">{number}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}
